import { HintsAccordionItem } from "../HintsAccordionItem";
import { HintsLink } from "../HintsLink";

interface Props {
  lang: string;
  openIndex: number | null;
  toggle: (index: number) => void;
  email: string;
  phone: string;
  openingHours?: string;
}

const CommonFaqs = ({ lang, openIndex, toggle, email, phone, openingHours }: Props) => {
  if (lang === "de") {
    return (
      <div className="flex flex-col">
        <h1 className="font-semibold text-foreground/90 text-2xl leading-[28px] tracking-[-0.015em] text-center mb-6">
          Allgemeine FAQ
        </h1>
        {openingHours && (
          <HintsAccordionItem
            onToggle={() => toggle(201)}
            isOpen={openIndex === 201}
            header="Was sind die Öffnungszeiten?"
          >
            <p>Unsere Öffnungszeiten sind {openingHours}.</p>
          </HintsAccordionItem>
        )}
        <HintsAccordionItem
          onToggle={() => toggle(0)}
          isOpen={openIndex === 0}
          header="Was ist der Zweck dieser App?"
        >
          <p>
            Mit der App kannst du intelligente Schließfächer für die private
            Übergabe, die vorübergehende Lagerung, den Verleih von Ausrüstung
            oder die Paketzustellung reservieren. Sie bietet dir eine sichere,
            flexible und bequeme Lösung, die auf deine Bedürfnisse zugeschnitten
            ist.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(1)}
          isOpen={openIndex === 1}
          header="Wie kann ich eine Buchung in der App erstellen und starten?"
        >
          <p>
            Besuche die Homepage und wähle den gewünschten Dienst. Folge den
            Anweisungen für die Reservierung von Schließfächern oder die Miete
            von Ausrüstung (CHECK). Du kannst Buchungen verwalten und erhältst
            Unterstützung direkt in der App.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(2)}
          isOpen={openIndex === 2}
          header="Welche Standorte gibt es?"
        >
          <p>
            Sobald du einen bestimmten Dienst ausgewählt hast, kannst du die
            verfügbaren Standorte in der Standortauswahl finden. Klicke einfach
            auf „Standorte“, um alle verfügbaren Standorte in deiner Nähe zu
            sehen.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(3)}
          isOpen={openIndex === 3}
          header="Wie bezahle ich die Schließfachdienste?"
        >
          <p>
            Zahlungen kannst du sicher innerhalb der App über Stripe mit deiner
            bevorzugten Zahlungsmethode vornehmen. Preisdetails siehst du
            während des Buchungsvorgangs.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(4)}
          isOpen={openIndex === 4}
          header="Gibt es Einschränkungen, was ich in einem Schließfach aufbewahren oder abgeben kann?"
        >
          <p>
            Ja, Schließfächer dürfen nicht für verderbliche, gefährliche oder
            illegale Gegenstände genutzt werden. Detaillierte Richtlinien
            findest du in den Allgemeinen Geschäftsbedingungen der App.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(5)}
          isOpen={openIndex === 5}
          header="Welche Sicherheitsmaßnahmen gibt es für Schließfächer?"
        >
          <p>
            Um sicherzustellen, dass nur autorisierte Benutzer Zugriff auf die
            Schließfächer haben, gibt es mehrere Sicherheitsebenen:
            <br />
            E-Mail-Verifizierung: Du musst deine E-Mail-Adresse während der
            Nutzung des Dienstes verifizieren.
            <br />
            One-Time Password (OTP): Bei privaten Übergaben wird ein einmaliges
            OTP an die E-Mail des Empfängers gesendet. Dieses muss eingegeben
            werden, um auf das Schließfach zuzugreifen.
            <br />
            QR-Code-Scan: An der Schließfachstation musst du einen QR-Code
            scannen, um zu bestätigen, dass du dich am richtigen Ort befindest,
            bevor du das Schließfach öffnen kannst.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(6)}
          isOpen={openIndex === 6}
          header="Was passiert, wenn ich technische Probleme mit der App oder dem Schließfach habe?"
        >
          <p>
            Wenn du Probleme mit der App oder dem Schließfach hast, probiere
            bitte Folgendes: Schließe die Registerkarte und öffne unsere
            Webanwendung erneut in einem Inkognito-Fenster. Falls das Problem
            weiterhin besteht, kontaktiere unser Support-Team per E-Mail{" "}
            <HintsLink href={`mailto:${email}`}>{email}</HintsLink> oder über
            die Hotline <HintsLink href={`tel:${phone}`}>{phone}</HintsLink>.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(7)}
          isOpen={openIndex === 7}
          header="Kann ich meine Buchung ändern oder stornieren?"
        >
          <p>
            Buchungen können in der Regel nicht geändert oder storniert werden.
            Für Stornierungen wende dich bitte an die Support-Hotline{" "}
            <HintsLink href={`tel:${phone}`}>{phone}</HintsLink>.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(8)}
          isOpen={openIndex === 8}
          header="Was soll ich tun, wenn sich meine Pläne in letzter Minute ändern?"
        >
          <p>
            Für private Übergaben, Geräteverleih oder vorübergehende Lagerung
            kontaktiere bitte die Support-Hotline{" "}
            <HintsLink href={`tel:${phone}`}>{phone}</HintsLink>. Änderungen
            sind zwar nicht immer möglich, aber unser Team wird dir helfen, eine
            Lösung zu finden, wenn es geht.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(9)}
          isOpen={openIndex === 9}
          header="Sind meine persönlichen Daten sicher?"
        >
          <p>
            Ja, wir halten uns an strenge Datenschutzrichtlinien. Alle deine
            persönlichen Daten werden verschlüsselt und sicher in
            Übereinstimmung mit den geltenden Vorschriften behandelt.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(10)}
          isOpen={openIndex === 10}
          header="Wie kann ich den Kundendienst kontaktieren?"
        >
          <p>
            Du kannst unser Support-Team über den Hilfebereich der App, per
            E-Mail an <HintsLink href={`mailto:${email}`}>{email}</HintsLink>{" "}
            oder über die Support-Hotline{" "}
            <HintsLink href={`tel:${phone}`}>{phone}</HintsLink> erreichen.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(11)}
          isOpen={openIndex === 11}
          header="Überprüfe vor Beginn das Equipment"
        >
          <p>
            Bitte stelle sicher, dass du alle benötigten Geräte und Zubehörteile
            vor Beginn der Nutzung überprüfst. Bei Fragen oder Problemen kannst
            du unser Support-Team über den Hilfebereich der App, per E-Mail an{" "}
            <HintsLink href={`mailto:${email}`}>{email}</HintsLink> oder über
            die Support-Hotline{" "}<HintsLink href={`tel:${phone}`}>{phone}</HintsLink>{" "}
            erreichen.
          </p>
        </HintsAccordionItem>
      </div>
    );
  } else {
    return (
      <div className="flex flex-col">
        <h1 className="font-semibold text-foreground/90 text-2xl leading-[28px] tracking-[-0.015em] text-center mb-6">
          FAQs
        </h1>
        <HintsAccordionItem
          onToggle={() => toggle(0)}
          isOpen={openIndex === 0}
          header="What is the purpose of this app?"
        >
          <p>
            With the app, you can reserve smart lockers for private handovers,
            temporary storage, equipment rental, or package delivery. It offers
            you a secure, flexible, and convenient solution tailored to your
            needs.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(1)}
          isOpen={openIndex === 1}
          header="How can I create and start a booking in the app?"
        >
          <p>
            Visit the homepage and select the desired service. Follow the
            instructions for reserving lockers or renting equipment. You can
            manage bookings and receive support directly in the app.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(2)}
          isOpen={openIndex === 2}
          header="What locations are available?"
        >
          <p>
            Once you have selected a specific service, you can find the
            available locations in the location selection. Simply click on
            "Locations" to see all available locations near you.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(3)}
          isOpen={openIndex === 3}
          header="How do I pay for locker services?"
        >
          <p>
            You can make payments securely within the app via Stripe with your
            preferred payment method. You can see pricing details during the
            booking process.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(4)}
          isOpen={openIndex === 4}
          header="Are there restrictions on what I can store or deposit in a locker?"
        >
          <p>
            Yes, lockers may not be used for perishable, dangerous, or illegal
            items. You can find detailed guidelines in the app's terms and
            conditions.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(5)}
          isOpen={openIndex === 5}
          header="What security measures are there for lockers?"
        >
          <p>
            To ensure that only authorized users have access to the lockers,
            there are multiple security levels:
            <br />
            Email Verification: You must verify your email address while using
            the service.
            <br />
            One-Time Password (OTP): For private handovers, a one-time OTP is
            sent to the recipient's email. This must be entered to access the
            locker.
            <br />
            QR Code Scan: At the locker station, you must scan a QR code to
            confirm that you are in the right location before you can open the
            locker.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(6)}
          isOpen={openIndex === 6}
          header="What happens if I have technical problems with the app or locker?"
        >
          <p>
            If you have problems with the app or locker, please try the
            following: Close the tab and reopen our web application in an
            incognito window. If the problem persists, contact our support team
            via email <HintsLink href={`mailto:${email}`}>{email}</HintsLink> or
            through the hotline{" "}
            <HintsLink href={`tel:${phone}`}>{phone}</HintsLink>.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(7)}
          isOpen={openIndex === 7}
          header="Can I change or cancel my booking?"
        >
          <p>
            Bookings generally cannot be changed or canceled. For cancellations,
            please contact the support hotline{" "}
            <HintsLink href={`tel:${phone}`}>{phone}</HintsLink>.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(8)}
          isOpen={openIndex === 8}
          header="What should I do if my plans change at the last minute?"
        >
          <p>
            For private handovers, equipment rental, or temporary storage,
            please contact the support hotline{" "}
            <HintsLink href={`tel:${phone}`}>{phone}</HintsLink>. Although
            changes are not always possible, our team will help you find a
            solution if possible.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(9)}
          isOpen={openIndex === 9}
          header="Is my personal data secure?"
        >
          <p>
            Yes, we adhere to strict privacy policies. All your personal data is
            encrypted and handled securely in accordance with applicable
            regulations.
          </p>
        </HintsAccordionItem>
        <HintsAccordionItem
          onToggle={() => toggle(10)}
          isOpen={openIndex === 10}
          header="How can I contact customer service?"
        >
          <p>
            You can reach our support team through the app's help section, via
            email at <HintsLink href={`mailto:${email}`}>{email}</HintsLink> ,
            or through the support hotline{" "}
            <HintsLink href={`tel:${phone}`}>{phone}</HintsLink>.
          </p>
        </HintsAccordionItem>
         <HintsAccordionItem
          onToggle={() => toggle(11)}
          isOpen={openIndex === 11}
          header="Check the equipment before you start using it"
        >
          <p>
            Please make sure to check all necessary equipment and accessories
            before you start using them. If you have any questions or issues,
            you can contact our support team through the app's help section,
            via email at <HintsLink href={`mailto:${email}`}>{email}</HintsLink>{" "}
            or through the support hotline{" "}<HintsLink href={`tel:${phone}`}>{phone}</HintsLink>.
          </p>
        </HintsAccordionItem>
      </div>
    );
  }
};

export default CommonFaqs;
